import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { getToken, refreshToken, isAuthenticated } from '@/lib/keycloak'
import type { ApiResponse, ApiError } from '@/types'

// Base URL from config
const baseURL = window.CONFIG?.API_BASE_URL || 'https://diab.pratiscare.com/api'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Only attempt token refresh if user is authenticated
    if (isAuthenticated()) {
      await refreshToken(30)
    }

    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      console.warn('[API Client] No token available for request:', config.url)
    }

    console.log('[API Client] Request:', config.method?.toUpperCase(), config.url, {
      hasToken: !!token,
      isAuth: isAuthenticated()
    })

    return config
  },
  (error) => {
    console.error('[API Client] Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('[API Client] Response:', response.config.url, response.status, {
      dataKeys: response.data ? Object.keys(response.data) : 'null'
    })
    return response
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config

    // Handle 401 - try to refresh token
    if (error.response?.status === 401 && originalRequest) {
      try {
        const refreshed = await refreshToken(0)
        if (refreshed) {
          const token = getToken()
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        console.error('[API] Token refresh failed:', refreshError)
      }
    }

    // Format error
    const apiError: ApiError = {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.response?.data?.message || error.message || 'Une erreur est survenue',
      details: error.response?.data?.details,
    }

    return Promise.reject(apiError)
  }
)

export default apiClient

// Helper to unwrap response - handles both wrapped {data: T, success: true} and direct T formats
function unwrapResponse<T>(data: unknown): T {
  console.log('[API Client] Raw response:', data)
  // Check if response is wrapped in ApiResponse format
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
    console.log('[API Client] Unwrapping ApiResponse format')
    return (data as ApiResponse<T>).data
  }
  // Direct response format (e.g., from clinical-service)
  console.log('[API Client] Using direct format')
  return data as T
}

// Typed API helpers
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get(url, { params })
  return unwrapResponse<T>(response.data)
}

export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.post(url, data)
  return unwrapResponse<T>(response.data)
}

export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put(url, data)
  return unwrapResponse<T>(response.data)
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch(url, data)
  return unwrapResponse<T>(response.data)
}

export async function del<T>(url: string): Promise<T> {
  const response = await apiClient.delete(url)
  return unwrapResponse<T>(response.data)
}
